import { applyOffsets } from "@msuite/utils/incoming-invoices";
import { guardString } from "@msuite/utils/strings";
import type {
	IncomingInvoiceOffsetCandidateList,
	IncomingInvoiceOffsetCandidateListItem,
} from "@msuite/utils/types";

/** The picker's form: the outgoing invoices picked, in the order they are taken off. */
export interface PaymentOffsetsFormValues {
	outgoingInvoiceIds: string[];
}

/** One open outgoing invoice as the picker lists it, with what picking it does. */
export interface OffsetRow extends IncomingInvoiceOffsetCandidateListItem {
	/** Its place in the order picked, from 1; null while it is not picked. */
	position: number | null;
	/** The number of the other open invoice it is planned against, or null. */
	usedBy: string | null;
	/** What it takes off the payment; null while it is not picked or takes nothing. */
	appliedAmount: number | null;
	/** Picked, but the payment is covered before it is reached. */
	isUnused: boolean;
}

/**
 * The candidates with what the picks do to the payment — computed by `applyOffsets`, the rule the
 * server applies when the file is exported, so the picker shows what the file will carry.
 *
 * @param candidates - The open outgoing invoices to the supplier, oldest first.
 * @param pickedIds - The ones picked, in the order picked.
 * @param amount - What the payment comes to after Skonto, before any offset.
 * @returns The rows, what the picks take off in total, what is left to transfer, and whether a
 *   pick takes nothing.
 */
export function toOffsetRows(
	candidates: IncomingInvoiceOffsetCandidateList,
	pickedIds: string[],
	amount: number,
) {
	const result = applyOffsets(amount, toPicked(candidates, pickedIds));
	const applied = new Map(result.applied.map((offset) => [offset.id, offset.amount]));
	const unused = new Set(result.unused);
	let offsetCents = 0;
	for (const offset of result.applied) offsetCents += Math.round(offset.amount * 100);
	const rows: OffsetRow[] = candidates.map((candidate) => {
		const index = pickedIds.indexOf(candidate.id);
		return {
			...candidate,
			position: index === -1 ? null : index + 1,
			usedBy: candidate.plannedFor ? guardString(candidate.plannedFor.number) : null,
			appliedAmount: applied.get(candidate.id) ?? null,
			isUnused: unused.has(candidate.id),
		};
	});
	return {
		rows,
		offsetAmount: offsetCents / 100,
		remaining: result.remaining,
		hasUnused: unused.size > 0,
	};
}

/**
 * The candidates picked, in the order picked; an id no longer among the candidates is dropped.
 *
 * @param candidates - The open outgoing invoices to the supplier.
 * @param pickedIds - The ones picked, in the order picked.
 */
export function toPicked(candidates: IncomingInvoiceOffsetCandidateList, pickedIds: string[]) {
	const byId = new Map(candidates.map((candidate) => [candidate.id, candidate]));
	const picked: IncomingInvoiceOffsetCandidateList = [];
	for (const id of pickedIds) {
		const candidate = byId.get(id);
		if (candidate) picked.push(candidate);
	}
	return picked;
}
